import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import Nav from './components/Nav';
import Footer from './components/Footer';
import ScrollProgress from './components/ScrollProgress';
import Home from './pages/Home';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import Download from './pages/Download';
import Docs from './pages/Docs';
import Help from './pages/Help';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Changelog from './pages/Changelog';
import Thanks from './pages/Thanks';
import Account from './pages/Account';
import AccountCallback from './pages/AccountCallback';
import AccountSso from './pages/AccountSso';
import Payments from './pages/Payments';
import NotFound from './pages/NotFound';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminCoupons from './pages/admin/AdminCoupons';
import AdminContacts from './pages/admin/AdminContacts';
import AdminEmail from './pages/admin/AdminEmail';
import AdminSecurity from './pages/admin/AdminSecurity';
import AdminServers from './pages/admin/AdminServers';
import AdminPayments from './pages/admin/AdminPayments';
import AdminRefunds from './pages/admin/AdminRefunds';
import AdminAlerts from './pages/admin/AdminAlerts';
import AdminSettings from './pages/admin/AdminSettings';
import AdminTrash from './pages/admin/AdminTrash';
import AdminApps from './pages/admin/AdminApps';
import AdminReviews from './pages/admin/AdminReviews';
import AdminLogs from './pages/admin/AdminLogs';
import AdminTraffic from './pages/admin/AdminTraffic';
import AdminAi from './pages/admin/AdminAi';
import NoticesBanner from './components/NoticesBanner';

/**
 * Route-level page transitions via AnimatePresence + a keyed motion.div.
 * Also resets scroll on route change so back/forward feels correct.
 * Deep links (/pricing, /docs/servers) work standalone as long as the
 * host rewrites unmatched paths to index.html — see vite.config comment.
 */
/**
 * Per-route SEO. SPA has one index.html, so we patch <title>, the meta
 * description, and canonical/og:url on every navigation. Keeps each page
 * distinct for crawlers and AI/generative engines (GEO).
 */
const SEO: Record<string, { t: string; d: string }> = {
  '/': { t: 'Vpnee — Zero-Log VPN with REALITY & Post-Quantum Encryption', d: 'Zero-log VPN on Xray REALITY with post-quantum ML-KEM VLESS. Free tier, no card, no email. Native Windows & Android apps.' },
  '/features': { t: 'Features — Vpnee VPN', d: 'REALITY transport, post-quantum ML-KEM VLESS, kill switch, split tunneling, zero-log. See what makes Vpnee fast and private.' },
  '/pricing': { t: 'Pricing — Vpnee VPN', d: 'Free tier with no card or email. Gold and Platinum annual plans for more data and servers. Simple, honest pricing.' },
  '/download': { t: 'Download Vpnee — Windows & Android VPN', d: 'Get the native Vpnee app for Windows 10+ and Android 6+. Real tunnel, no webview shim. Free tier works right after install.' },
  '/docs': { t: 'Docs — Vpnee VPN', d: 'Setup guides, server configuration, and troubleshooting for Vpnee on every platform.' },
  '/help': { t: 'Help & Support — Vpnee VPN', d: 'Searchable FAQ, quick fixes, and a direct line to a human. MTU issues, 2FA recovery, refunds, coupons, and more.' },
  '/about': { t: 'About — Vpnee VPN', d: 'Vpnee is built by TNIDU Inc. — a zero-log VPN focused on real privacy and post-quantum security.' },
  '/privacy': { t: 'Privacy Policy — Vpnee VPN', d: 'Vpnee is zero-log by design. Read exactly what we do and do not store.' },
  '/terms': { t: 'Terms of Service — Vpnee VPN', d: 'The terms that govern your use of Vpnee.' },
  '/changelog': { t: 'Changelog — Vpnee VPN', d: 'Every Vpnee release and what changed, newest first.' },
  '/account': { t: 'Your Account — Vpnee VPN', d: 'Manage your Vpnee plan, devices, payments, and support.' },
  '/payments': { t: 'Checkout — Vpnee VPN', d: 'Secure one-shot checkout for your Vpnee plan. Card, OnePay, or Alipay.' },
};

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [location.pathname, location.hash]);

  // Per-route SEO tags
  useEffect(() => {
    const seo = SEO[location.pathname] || SEO['/'];
    document.title = seo.t;
    setMeta('description', seo.d);
    setMeta('og:title', seo.t, 'property');
    setMeta('og:description', seo.d, 'property');
    setMeta('og:url', 'https://vpnee.tnidu.com' + location.pathname, 'property');
    setMeta('twitter:title', seo.t);
    setMeta('twitter:description', seo.d);
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.rel = 'canonical'; document.head.appendChild(link); }
    link.href = 'https://vpnee.tnidu.com' + location.pathname;
  }, [location.pathname]);

  /**
   * OAuth hop. Backend redirects to `/?oauth_email=…&oauth_provider=…&oauth_token=…`
   * (never to /account/callback directly — vnav.lk has no SPA history-fallback,
   * so any nested path 404s on hard load). Re-dispatch to the real callback
   * route client-side; from there `AccountCallback` finalises via /v2/social.
   */
  useEffect(() => {
    if (location.pathname !== '/') return;
    const q = new URLSearchParams(location.search);
    const err = q.get('oauth_error');
    if (err === 'not_registered') {
      const email = q.get('oauth_email') || '';
      alert(`No account found with Google email ${email}. Please sign up first.`);
      navigate('/account', { replace: true });
      return;
    }
    const email = q.get('oauth_email');
    const provider = q.get('oauth_provider');
    const token = q.get('oauth_token');
    if (email && token) {
      const to = `/account/callback?email=${encodeURIComponent(email)}&provider=${encodeURIComponent(provider || 'google')}&token=${encodeURIComponent(token)}`;
      navigate(to, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const isAdmin = location.pathname.startsWith('/admin');
  return (
    <>
      {!isAdmin && <ScrollProgress />}
      {!isAdmin && <Nav />}
      {!isAdmin && <NoticesBanner />}
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.28, ease: [0.22, 0.6, 0.36, 1] }}
          className="relative"
        >
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/download" element={<Download />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/docs/:slug" element={<Docs />} />
            <Route path="/help" element={<Help />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/thanks" element={<Thanks />} />
            <Route path="/thank" element={<Thanks />} />
            <Route path="/account" element={<Account />} />
            <Route path="/account/callback" element={<AccountCallback />} />
            <Route path="/account/sso" element={<AccountSso />} />
            <Route path="/payments" element={<Payments />} />
            {/* Admin — full panel inside Vite: login + MFA, perfect glass/rose UI */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:id" element={<AdminUserDetail />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="refunds" element={<AdminRefunds />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="email" element={<AdminEmail />} />
              <Route path="security" element={<AdminSecurity />} />
              <Route path="servers" element={<AdminServers />} />
              <Route path="alerts" element={<AdminAlerts />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="trash" element={<AdminTrash />} />
              <Route path="apps" element={<AdminApps />} />
              <Route path="submissions" element={<AdminReviews />} />
              <Route path="appeals" element={<AdminReviews />} />
              <Route path="logs" element={<AdminLogs />} />
              <Route path="traffic" element={<AdminTraffic />} />
              <Route path="ai" element={<AdminAi />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </motion.main>
      </AnimatePresence>
      {!isAdmin && <Footer />}
    </>
  );
}
